import { EProjectType, unit } from '../../../enum/project';
import { ProjectImages } from '../../../types/UnifiedPdfImageTypes';
import { IAirportImages, IRailwayImages } from '../../../services/PdfImageCaptureService';

// Re-export for backward compatibility
export type { IAirportImages, IRailwayImages } from '../../../services/PdfImageCaptureService';

/**
 * Project Info interface for PDF components
 * Replaces projectInfo.data as any type assertions
 */
export interface IPdfProjectInfo {
    projectName?: string;
    nrProject?: string;
    unit?: unit;
    projectStatus?: string;
    projectType?: string;
    userId?: number;
    appId?: number;
    lastUpdateDate?: Date;
    owner?: string;
    designerFirmName?: string;
    designerFirmCountry?: string;
    designerFirmCity?: string;
    coordinates?: [number | undefined, number | undefined];
    productCountry?: string | null;
    products?: string[];
    notes?: string;
    mapImage?: string;
    method?: string;
    giroud?: Record<string, unknown>;
    leng?: Record<string, unknown>;
    geoGrid?: Record<string, unknown>;
    [key: string]: unknown;
}

/**
 * Redux Project State interface
 */
export interface IReduxProjectState {
    data?: {
        projectInfo?: {
            data?: IPdfProjectInfo;
        };
        guid?: string;
        type?: EProjectType;
        projectName?: string;
        unit?: unit;
        aashtoResults?: unknown;
        geogridHasBeenRan?: boolean;
        owner?: string;
        designerFirmName?: string;
        designerFirmCountry?: string;
        productCountry?: string | null;
        products?: string[];
        notes?: string;
        mapImage?: string;
        idUser?: number;
        idApp?: number;
        [key: string]: unknown;
    };
}

/**
 * Container Yard / Parking Deck / Unpaved Images
 */
export interface IContainerYardImages {
    tireImg1: string;
    tireImg2: string;
    tireImg3?: string;
    chartImg: string;
}

/**
 * Paved Project Images
 */
export interface IPavedImages {
    tireImageUnreinforced: string;
    tireImageReinforced: string;
    tireImageGeogrid: string;
    chartImg: string;
    trafficData?: {
        unstabilized?: {
            passesFirstYear?: number;
            w18?: number;
            totalEsal?: number;
            tbr?: number;
        };
        increasedPassages?: {
            passesFirstYear?: number;
            w18?: number;
            totalEsal?: number;
            tbr?: number;
        };
        optimized?: {
            passesFirstYear?: number;
            w18?: number;
            totalEsal?: number;
            tbr?: number;
        };
    };
    // Dynamic design sections - optional for backward compatibility
    surfaceDesignImages?: {
        canvas1?: string;
        canvas2?: string;
        canvas3?: string;
        tableData1?: {
            passesFirstYear?: number;
            w18?: number;
            thickness?: number;
        };
        tableData2?: {
            passesFirstYear?: number;
            newW18?: number;
            tbr?: number;
        };
        tableData3?: {
            passesFirstYear?: number;
            w18?: number;
            thicknessSaving?: number;
        };
    };
    baseDesignImages?: {
        canvas1?: string;
        canvas2?: string;
        canvas3?: string;
        tableData1?: {
            passesFirstYear?: number;
            w18?: number;
            thickness?: number;
        };
        tableData2?: {
            passesFirstYear?: number;
            newW18?: number;
            tbr?: number;
        };
        tableData3?: {
            passesFirstYear?: number;
            w18?: number;
            thicknessSaving?: number;
        };
    };
    subbaseDesignImages?: {
        canvas1?: string;
        canvas2?: string;
        canvas3?: string;
        tableData1?: {
            passesFirstYear?: number;
            w18?: number;
            thickness?: number;
        };
        tableData2?: {
            passesFirstYear?: number;
            newW18?: number;
            tbr?: number;
        };
        tableData3?: {
            passesFirstYear?: number;
            w18?: number;
            thicknessSaving?: number;
        };
    };
}

/**
 * Airport Project Images (re-export from service)
 */
export type IAirportImagesType = IAirportImages;

/**
 * Railway Project Images (re-export from service)
 */
export type IRailwayImagesType = IRailwayImages;

/**
 * Unified PDF images interface for new implementation
 */
export interface IUnifiedPdfProps {
    projectType: EProjectType;
    projectImages?: ProjectImages | null;
    isGeneratingPdfImages?: boolean;
    pdfImageError?: Error | null;
    geoGridLayersImage?: string;
    isGeneratingGeoGridPdfImages?: boolean;
    geoGridPdfError?: Error | null;
}

/**
 * Main ButtonPanel Props Interface
 */
export interface IButtonPanelProps extends Partial<IUnifiedPdfProps> {
    showSave?: boolean;
    showExportPdf?: boolean;
    showExportGeoGridPdf?: boolean;
    showExportCombinedPdf?: boolean;
    showGeoGrid?: boolean;
    showExecute?: boolean;
    showExecuteGeoGrid?: boolean;
    onSave?: () => void;
    onExecute?: () => void;
    onExportToPDF?: () => Promise<void>;
    saveDisabled?: boolean;
    showGeoGridDisabled?: boolean;
    executeDisabled?: boolean;
    executeGeoGridDisabled?: boolean;
    useUnifiedProps?: boolean;
    // NEW: Read-only mode flag (hides Save/Update buttons)
    isReadOnly?: boolean;
}

/**
 * Combined PDF props for different project types
 */
export interface ICombinedPdfProps {
    output?: unknown;
    projectInfo?: IPdfProjectInfo;
    dynamicProject?: unknown;
    t?: (key: string) => string;
    tireImageUnreinforced?: string;
    tireImageReinforced?: string;
    tireImageGeotextile?: string;
    chartImage?: string;
    geoGridData?: unknown;
    geogridImage?: string;
    images?: unknown;
    [key: string]: unknown;
}

/**
 * Type Guards
 */
export const isUnifiedProps = (props: IButtonPanelProps): props is IUnifiedPdfProps & IButtonPanelProps => {
    return props.useUnifiedProps === true && 'projectType' in props && props.projectType !== undefined;
};

export const hasValidProjectInfo = (projectInfo: unknown): projectInfo is { data: IPdfProjectInfo } => {
    return projectInfo !== null && projectInfo !== undefined && typeof projectInfo === 'object' && 'data' in projectInfo && projectInfo.data !== null && projectInfo.data !== undefined;
};

export const isValidProjectState = (project: unknown): project is IReduxProjectState => {
    return project !== null && project !== undefined && typeof project === 'object' && 'data' in project;
};
